import { z } from 'zod';
import { LeadStatus } from '../enums.js';
import { CursorPagination } from './common.js';
/**
 * Filter convention:
 *   - `categories` is OR semantic (each lead has exactly one category, so
 *     multi-select means "match any one of these")
 *   - `tags` is AND semantic (a lead has many tags; multi-select means
 *     "must contain all of these" — for narrowing down)
 *   - Both lists, when non-empty, AND together with each other.
 *
 * Wire as repeated query params OR a single comma-separated string —
 * `csvList` below handles both shapes.
 */
const csvList = z.preprocess((v) => {
    if (v === undefined || v === null || v === '')
        return [];
    if (Array.isArray(v))
        return v.flatMap((s) => String(s).split(','));
    if (typeof v === 'string')
        return v.split(',');
    return [];
}, z.array(z.string().trim().min(1)));
export const ListLeadsQuery = CursorPagination.extend({
    status: LeadStatus.optional(),
    assignedToMe: z.boolean().optional(),
    tenantId: z.string().uuid().optional(),
    /** Filter by one or more categories — OR semantic. */
    categories: csvList.optional(),
    /** Filter by tags the lead must ALL contain — AND semantic. */
    tags: csvList.optional(),
});
/** Aggregated category + tag counts across leads visible to the caller —
 *  drives the click-to-filter chip cluster in the leads page UI. */
export const LeadTagStatsResponse = z.object({
    categories: z.array(z.object({ name: z.string(), count: z.number().int() })),
    tags: z.array(z.object({ name: z.string(), count: z.number().int() })),
});
export const LeadSummary = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    targetUserId: z.string(),
    status: LeadStatus,
    version: z.number().int(),
    assignedSalesId: z.string().uuid().nullable(),
    assignedAt: z.string().datetime().nullable(),
    sourceGroupId: z.string().uuid(),
    triggeredByAccountId: z.string().uuid(),
    /** Coarse category produced by L2/L3 (e.g., '金融', '建材', '服装'). Null on legacy leads. */
    category: z.string().nullable(),
    /** Multi-label tags produced by L2/L3 (e.g., ['USDT','求购','量大']). */
    tags: z.array(z.string()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const LeadDetail = LeadSummary.extend({
    llmJudgmentJson: z.record(z.unknown()),
    triggerMessageIds: z.array(z.string().uuid()),
    conversationId: z.string().uuid().nullable(),
});
export const ClaimLeadRequest = z.object({
    expectedVersion: z.number().int(),
});
export const ClaimLeadResponse = z.object({
    lead: LeadSummary,
});
export const ReleaseLeadRequest = z.object({
    expectedVersion: z.number().int(),
    reason: z.string().optional(),
});
export const CloseLeadRequest = z.object({
    expectedVersion: z.number().int(),
    outcome: z.enum(['closed_won', 'closed_lost']),
    reason: z.string().optional(),
});
/** One trigger message expanded for the lead detail panel. */
export const LeadTriggerMessage = z.object({
    id: z.string().uuid(),
    tgChatId: z.string(),
    tgMessageId: z.string(),
    senderUserId: z.string(),
    senderUsername: z.string().nullable(),
    senderFirstName: z.string().nullable(),
    text: z.string(),
    ts: z.string().datetime(),
});
export const LeadTriggersResponse = z.object({
    items: z.array(LeadTriggerMessage),
});
//# sourceMappingURL=lead.js.map